import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { ShieldCheck, CheckCircle2, AlertTriangle, FileDown, ExternalLink } from 'lucide-react';

const SignHours = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ signer_name: '', signer_license: '', signer_title: '', agreed: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/internship-portal/sign/${token}`);
      setData(res.data);
      if (res.data.status === 'signed') setDone(true);
    } catch (e) { setData({ status: 'invalid' }); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { document.title = 'Sign Supervised Hours | I Need A Social Worker'; load(); }, [load]);

  const submit = async () => {
    if (!form.signer_name.trim()) { toast.error('Please enter your full name.'); return; }
    if (!form.agreed) { toast.error('Please confirm the verification statement.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/internship-portal/sign/${token}`, form);
      toast.success('Signature recorded. Thank you!');
      setDone(true);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit signature.'); }
    finally { setSubmitting(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API_URL}/internship-portal/sign/${token}/report.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'signed_internship_hours_report.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download the report.'); }
  };

  if (loading) return <Shell><p className="text-gray-500 text-center py-10">Loading…</p></Shell>;

  if (!data || ['invalid', 'revoked', 'expired'].includes(data.status)) {
    const msg = data?.status === 'expired' ? 'This signing link has expired.'
      : data?.status === 'revoked' ? 'This request was withdrawn by the member.'
        : 'This signing link is invalid.';
    return (
      <Shell>
        <div className="text-center py-10" data-testid="sign-inactive">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{msg}</p>
          <p className="text-gray-500 text-sm mt-1">Please ask the member to send a new request.</p>
        </div>
      </Shell>
    );
  }

  const s = data.summary || {};
  const req = data.requirement;
  const pw = data.pathway;

  return (
    <Shell>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-6 h-6 text-secondary" />
        <h1 className="font-serif text-2xl font-bold text-primary">Supervised-Hours Sign-Off</h1>
      </div>
      <p className="text-gray-600 text-sm mb-6"><strong>{data.member_name}</strong> has asked you to review and sign their supervised-experience hour report.</p>

      {data.member_message && (
        <p className="text-sm text-gray-600 italic border-l-4 border-secondary pl-3 mb-6" data-testid="sign-member-message">&ldquo;{data.member_message}&rdquo;</p>
      )}

      {/* Pathway + requirement */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <Box title="Pathway">
          {pw ? (
            <>
              <p className="text-sm text-gray-700 font-medium">{pw.target_license_name || pw.target_license_code}</p>
              <p className="text-xs text-gray-500">{pw.state || 'National (CSWE)'} · {pw.pathway_type === 'academic' ? 'Academic Field Placement' : 'Post-Graduate Supervised Experience'}</p>
              {pw.school_name && <p className="text-xs text-gray-500 mt-1">{pw.school_name}</p>}
            </>
          ) : <p className="text-sm text-gray-400">No pathway set.</p>}
        </Box>
        <Box title="Progress">
          <p className="text-sm text-gray-700"><strong>{s.completed_hours || 0}</strong> / {s.required_hours || 0} hours ({s.percent_complete || 0}%)</p>
          {s.supervision_required > 0 && <p className="text-xs text-gray-500">Supervision: {s.supervision_hours || 0} / {s.supervision_required}</p>}
          {s.direct_client_required > 0 && <p className="text-xs text-gray-500">Direct client: {s.direct_client_hours || 0} / {s.direct_client_required}</p>}
          {req?.official_source_url && <a href={req.official_source_url} target="_blank" rel="noopener noreferrer" className="text-secondary text-xs inline-flex items-center gap-1 mt-1">{req.official_source_name || 'Official requirements'} <ExternalLink className="w-3 h-3" /></a>}
        </Box>
      </div>

      {/* Logs */}
      <div className="border rounded-xl overflow-hidden mb-6">
        <div className="overflow-x-auto max-h-80">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0"><tr className="text-left text-gray-500">
              <th className="py-2 px-3">Date</th><th className="py-2 px-3">Hrs</th><th className="py-2 px-3">Supv</th>
              <th className="py-2 px-3">Category</th><th className="py-2 px-3">Activities</th><th className="py-2 px-3">Status</th>
            </tr></thead>
            <tbody>
              {(data.logs || []).map((l, i) => (
                <tr key={i} className="border-t" data-testid={`sign-log-${i}`}>
                  <td className="py-2 px-3 whitespace-nowrap">{l.date_worked || l.week_start || '—'}</td>
                  <td className="py-2 px-3">{l.total_hours}</td>
                  <td className="py-2 px-3">{l.supervision_hours || 0}</td>
                  <td className="py-2 px-3 whitespace-nowrap text-gray-600">{l.category || '—'}</td>
                  <td className="py-2 px-3 max-w-[260px] truncate">{l.activities || '—'}</td>
                  <td className="py-2 px-3">{l.status}</td>
                </tr>
              ))}
              {(data.logs || []).length === 0 && <tr><td colSpan="6" className="py-4 text-center text-gray-400">No logs.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {done ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center" data-testid="sign-complete">
          <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
          <p className="font-semibold text-green-800">This report has been signed{data.signer_name ? ` by ${data.signer_name}` : ''}.</p>
          <p className="text-sm text-green-700 mt-1">Thank you for verifying these hours.</p>
          <Button className="bg-primary text-white mt-4" onClick={downloadPdf} data-testid="sign-download-pdf"><FileDown className="w-4 h-4 mr-1" /> Download Signed PDF</Button>
        </div>
      ) : (
        <div className="bg-primary/5 border border-primary/15 rounded-xl p-5" data-testid="sign-form">
          <h2 className="font-semibold text-primary mb-3">Verify &amp; Sign</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs text-gray-500 mb-1 block">Your Full Name</Label><Input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} data-testid="signer-name" /></div>
            <div><Label className="text-xs text-gray-500 mb-1 block">License # (optional)</Label><Input value={form.signer_license} onChange={(e) => setForm({ ...form, signer_license: e.target.value })} data-testid="signer-license" /></div>
            <div><Label className="text-xs text-gray-500 mb-1 block">Title (optional)</Label><Input value={form.signer_title} onChange={(e) => setForm({ ...form, signer_title: e.target.value })} data-testid="signer-title" /></div>
          </div>
          <label className="flex items-start gap-2 mt-4 cursor-pointer">
            <Checkbox checked={form.agreed} onCheckedChange={(v) => setForm({ ...form, agreed: !!v })} data-testid="signer-agree" />
            <span className="text-sm text-gray-600">I verify that the supervised hours recorded above were completed under my supervision and are accurate to the best of my knowledge.</span>
          </label>
          <Button className="bg-primary text-white mt-4" onClick={submit} disabled={submitting} data-testid="signer-submit"><ShieldCheck className="w-4 h-4 mr-1" /> {submitting ? 'Submitting…' : 'Sign Report'}</Button>
        </div>
      )}
    </Shell>
  );
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gray-50 py-10 px-4">
    <div className="max-w-3xl mx-auto bg-white rounded-2xl border shadow-sm p-6 sm:p-8">
      {children}
      <p className="text-xs text-gray-400 mt-8 border-t pt-4">I Need A Social Worker · Internship &amp; Supervised Experience Portal. This record is for documentation convenience and is not legal advice.</p>
    </div>
  </div>
);

const Box = ({ title, children }) => (
  <div className="border rounded-xl p-4">
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
    {children}
  </div>
);

export default SignHours;
